import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Email Verification Page
 * Shows after user signs up to prompt them to verify their email
 */
const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
  const [isResending, setIsResending] = useState(false);
  
  const email = searchParams.get('email') || '';
  
  const handleResendEmail = async () => {
    if (!email) {
      setStatus({ type: 'error', message: 'Email address is missing.' });
      return;
    }
    
    setIsResending(true);
    setStatus({ type: null, message: '' });
    
    try {
      const response = await fetch('/api/v1/email/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatus({ 
          type: 'success', 
          message: data.data.message || 'New verification email sent. It may take a minute to arrive.' 
        });
      } else {
        setStatus({ 
          type: 'error', 
          message: data.error || 'Something went wrong sending the email. Please try again.' 
        });
      }
    } catch (err: any) {
      setStatus({ 
        type: 'error', 
        message: err.message || 'Failed to send verification email. Please try again.' 
      });
    } finally {
      setIsResending(false);
    }
  };
  
  const handleChangeEmail = () => {
    navigate('/auth', { replace: true });
  };
  
  const handleContactSupport = () => {
    window.location.href = 'mailto:support@thetraders.ai?subject=Email Verification Help';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-[640px]">
        <div className="card border border-slate-800 rounded-[1.75rem] p-7 sm:p-9 md:p-10 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-950 shadow-[0_24px_40px_rgba(0,0,0,0.55)]">
          <header className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-[20px] bg-gradient-to-br from-emerald-500/20 via-transparent to-transparent border border-slate-800 mb-4">
              <span className="text-2xl">📩</span>
            </div>
            <h1 className="text-[1.4rem] font-semibold mb-2 text-gray-200">
              Verify your email to activate your account
            </h1>
            <p className="text-[0.95rem] text-gray-400 leading-relaxed">
              We've sent a secure verification link to{' '}
              <span className="text-white font-medium">{email || 'your email address'}</span>. 
              Please check your inbox and click the link to continue.
            </p>
          </header>
          
          <section className="mt-5 mb-3">
            <p className="text-[0.9rem] font-semibold mb-1 text-gray-200">
              What to do next:
            </p>
            <ol className="pl-5 text-[0.9rem] text-blue-100 leading-relaxed list-decimal space-y-0.5">
              <li>Open your email app or inbox.</li>
              <li>Look for an email from <strong className="text-gray-200">TheTraders.ai</strong>.</li>
              <li>Click the <strong className="text-gray-200">"Verify email"</strong> button inside.</li>
            </ol>
            <p className="text-xs text-gray-400 mt-2">
              Didn't see it after a minute? Check your <strong className="text-gray-200">Spam</strong> or{' '}
              <strong className="text-gray-200">Promotions</strong> folder as well.
            </p>
          </section>
          
          {status.type && (
            <p className={`mt-2.5 text-xs text-center ${
              status.type === 'success' ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {status.message}
            </p>
          )}
          
          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={handleResendEmail}
              disabled={isResending || !email}
              className="flex-1 rounded-full px-4 py-2.5 text-sm font-medium bg-emerald-500 text-slate-950 border border-transparent shadow-[0_10px_18px_rgba(34,197,94,0.28)] hover:bg-emerald-600 hover:shadow-[0_14px_22px_rgba(34,197,94,0.35)] hover:-translate-y-[1px] active:translate-y-0 active:shadow-[0_4px_10px_rgba(34,197,94,0.4)] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none"
            >
              {isResending ? 'Sending...' : 'Resend verification email'}
            </button>
            <button
              type="button"
              onClick={handleChangeEmail}
              className="flex-1 rounded-full px-4 py-2.5 text-sm font-medium bg-transparent text-gray-200 border border-slate-800 hover:bg-slate-950 hover:border-slate-700 transition-all duration-150"
            >
              Use a different email
            </button>
          </div>
          
          <p className="mt-4 text-xs text-gray-400 text-center leading-relaxed">
            Still can't find the email?{' '}
            <button
              type="button"
              onClick={handleContactSupport}
              className="bg-none border-none p-0 m-0 text-sky-300 font-inherit cursor-pointer underline underline-offset-[3px] decoration-1 hover:text-sky-200"
            >
              Contact support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

